import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from '@/i18n/translations';

import { RouterLinkAs } from '@/lib/router-link';
import { useReturnHereSearch } from '@/lib/return-here';
import {
  Body, Box, Button, Card, DelayedSkeleton, Divider, Eyebrow, I, Icon,
  LinkUnstyled, PanelHeaderSkeleton, Pill, Row, Skeleton,
  SkeletonText, Stack,
} from '@leanin/ui';
import type { ReactionCounts, ReactionKind } from '@leanin/shared/reactions';

import { useErrorMessage } from '@/lib/error-message';
import { trackResourceFileDownloaded } from '@/lib/analytics/events';
import { HostedVideoPlayer, VideoEmbedFrame } from '@/components/video/index';
import { DetailPage } from '@/components/page-shell/index';
import {
  PostActions,
  ReactionButton,
  ReactionSummary,
} from '@/components/posts/index';
import { buildResourceHeader } from './build-resource-header';
import { AuthorByline } from './AuthorByline';
import { CanonicalAttribution } from './CanonicalAttribution';
export interface RelatedResource {
  readonly id: string;
  readonly title: string;
}

// The aside's related list as its query left it. A failure is shown in the
// aside alone: the article is the page, the list is secondary to it.
export type RelatedResources =
  | { readonly status: 'pending' }
  | { readonly status: 'error'; readonly error: unknown }
  | { readonly status: 'success'; readonly items: ReadonlyArray<RelatedResource> };
import { AttachmentCard } from '@/components/cards/index';
import { LoadErrorState } from '@/components/states/index';
import { ArticleBody } from '@/components/resources/ArticleBody';
import { ResourceComments } from '@/components/resources/ResourceComments';
import styles from './ResourceArticleView.module.css';
import {
  type ResourceAttachmentView,
  type ResourceDesignedFor,
  type ResourceDetailView,
} from '@/lib/queries/resources';
import {
  useReactToResource,
  useSaveResource,
  useUnsaveResource,
} from '@/lib/mutations/resources';
import { ShareEntityButton } from '@/components/feed/ShareEntityButton';

interface Props {
  /** Adapted resource detail. `null` while the detail query is still loading
   *  — header, body card, and aside swap from skeleton stand-ins to real
   *  content when this resolves. */
  readonly resource: ResourceDetailView | null;
  readonly related: RelatedResources;
  /** False for anonymous viewers on the public route — member-only actions
   *  (save, react, comment) route to sign-in or hide; reading, sharing,
   *  and PDF download remain. */
  readonly signedIn: boolean;
  readonly viewerIsAdmin: boolean;
  readonly viewerCanEdit: boolean;
}

export function ResourceArticleView({
  resource,
  related,
  signedIn,
  viewerIsAdmin,
  viewerCanEdit,
}: Props) {
  const t = useTranslations('Resources');

  // ---------- Header ----------
  // Header chrome paints from first render: when `resource` is null the
  // builder emits skeleton title / subtitle / factoid placeholders + the
  // tone-tinted cover stand-in; once the detail lands, the real header takes
  // over with mutation-aware actions.
  const builtHeader = useMemo(() => {
    if (resource === null) return buildResourceHeaderSkeleton();
    return buildResourceHeader(resource, {
      actions: (
        <ResourceHeaderActions
          resource={resource}
          signedIn={signedIn}
          viewerCanEdit={viewerCanEdit}
          viewerIsAdmin={viewerIsAdmin}
        />
      ),
    }, t);
  }, [resource, signedIn, viewerCanEdit, viewerIsAdmin, t]);

  // ---------- Aside ----------
  const aside = buildAside(resource, related);

  return (
    <DetailPage
      header={builtHeader.header}
      {...(builtHeader.cover !== undefined ? { cover: builtHeader.cover } : {})}
      {...(aside !== null ? { aside: { modules: aside, narrow: 'stack', sticky: true } } : {})}
    >
      <DelayedSkeleton
        isLoading={resource === null}
        fallback={<ResourceArticleSkeleton />}
      >
        {resource !== null ? (
          <ResourceArticleBody resource={resource} signedIn={signedIn} />
        ) : null}
      </DelayedSkeleton>
    </DetailPage>
  );
}

// ── Header actions ────────────────────────────────────────────────────────

// Reaction-picker chip shared by the header actions and the bottom action
// bar — one popover-trigger implementation so the two can't drift.
function ResourceReactionChip({ resource }: { resource: ResourceDetailView }) {
  const describeError = useErrorMessage();
  const reactMutation = useReactToResource();

  const reactionCounts: ReactionCounts = resource.reactionCounts;
  const viewerReaction: ReactionKind | null = resource.viewerReaction;
  const reactionError =
    reactMutation.error !== null ? describeError(reactMutation.error) : null;

  const onReact = (kind: ReactionKind) => {
    reactMutation.mutate({ id: resource.id, kind });
  };

  return (
    <Row gap="sm" align="center">
      <ReactionButton variant="chip" target="article" mine={viewerReaction} onReact={onReact} />
      <ReactionSummary variant="row" counts={reactionCounts} mine={viewerReaction} />
      {reactionError !== null ? (
        <Body size="sm" color="destructive">
          {reactionError}
        </Body>
      ) : null}
    </Row>
  );
}

function ResourceHeaderActions({
  resource,
  signedIn,
  viewerCanEdit,
  viewerIsAdmin,
}: {
  resource: ResourceDetailView;
  signedIn: boolean;
  viewerCanEdit: boolean;
  viewerIsAdmin: boolean;
}) {
  const t = useTranslations('Resources');
  const saveMutation = useSaveResource();
  const unsaveMutation = useUnsaveResource();

  const toggleSaved = () => {
    if (resource.viewerSaved) {
      unsaveMutation.mutate(resource.id);
    } else {
      saveMutation.mutate({ id: resource.id, kind: resource.kind });
    }
  };

  return (
    <>
      {signedIn ? (
        <ResourceReactionChip resource={resource} />
      ) : (
        <ReactionSummary variant="row" counts={resource.reactionCounts} mine={null} />
      )}
      {signedIn ? (
        <Button
          variant={resource.viewerSaved ? 'primary' : 'secondary'}
          size="sm"
          icon={I.bookmark}
          onClick={toggleSaved}
        >
          {resource.viewerSaved ? t('detail.saved') : t('detail.save')}
        </Button>
      ) : null}
      <DownloadPdfButton resourceId={resource.id} variant="secondary" />
      <ShareEntityButton
        entity={{ kind: 'resource', id: resource.id }}
        variant="primary"
        size="sm"
        showLabel
      />
      {viewerCanEdit ? (
        <Button
          href={`/admin/resources/${resource.id}/edit`}
          linkAs={RouterLinkAs}
          variant="secondary"
          size="sm"
          icon={I.edit}
        >
          {t('detail.edit')}
        </Button>
      ) : null}
      {viewerIsAdmin ? (
        <Button
          href={`/admin/resources/${resource.id}`}
          linkAs={RouterLinkAs}
          variant="secondary"
          size="sm"
          icon={I.cog}
        >
          {t('detail.manage')}
        </Button>
      ) : null}
    </>
  );
}

// ── Article body (loaded) ─────────────────────────────────────────────────

function ResourceArticleBody({
  resource,
  signedIn,
}: {
  resource: ResourceDetailView;
  signedIn: boolean;
}) {
  const t = useTranslations('Resources');
  const navigate = useNavigate();
  const returnHereSearch = useReturnHereSearch();
  const saveMutation = useSaveResource();
  const unsaveMutation = useUnsaveResource();
  // Comments start expanded when the conversation already exists; a fresh
  // resource keeps the post-style closed-until-clicked behavior.
  const [commentsOpen, setCommentsOpen] = useState(resource.commentCount > 0);

  const toggleSaved = () => {
    if (resource.viewerSaved) {
      unsaveMutation.mutate(resource.id);
    } else {
      saveMutation.mutate({ id: resource.id, kind: resource.kind });
    }
  };

  // Anonymous viewers get functional-looking chips that route to the join
  // module (growth surface — existing members can switch to sign-in from
  // there) instead of silently doing nothing. The resource rides along as
  // `next`, so the member lands back on it once signed in or onboarded.
  const promptSignIn = () => void navigate({ to: '/get-started', search: returnHereSearch });

  const showComments = commentsOpen && signedIn;

  return (
    <Card pad="flush">
      <article>
        <Stack gap="xl" p="2xl">
          {resource.showAuthor ? <AuthorByline resource={resource} /> : null}
          <CanonicalAttribution canonicalUrl={resource.canonicalUrl} />
          <VideoEmbed resource={resource} />
          <ArticleBody source={resource.bodyMarkdown} />
          <Attachments resource={resource} />
        </Stack>
        <PostActions
          comments={resource.commentCount}
          commentsOpen={showComments}
          {...(signedIn
            ? {
                onComment: () => setCommentsOpen((o) => !o),
                onSave: toggleSaved,
                saved: resource.viewerSaved,
              }
            : { onComment: promptSignIn })}
          reactSlot={
            signedIn ? (
              <ResourceReactionChip resource={resource} />
            ) : (
              // Reacting needs an account: the same control, routed to sign-in.
              <Row gap="sm" align="center">
                <ReactionButton variant="chip" target="article" mine={null} onReact={promptSignIn} />
                <ReactionSummary variant="row" counts={resource.reactionCounts} mine={null} />
              </Row>
            )
          }
          extraSlot={<DownloadPdfButton resourceId={resource.id} variant="ghost" />}
          shareSlot={
            <ShareEntityButton
              entity={{ kind: 'resource', id: resource.id }}
              variant="ghost"
              size="sm"
            />
          }
        />
        {showComments ? (
          <>
            <Divider />
            <Box py="lg" px="2xl">
              <ResourceComments resourceId={resource.id} />
            </Box>
          </>
        ) : null}
      </article>
    </Card>
  );
}

// The endpoint answers with `Content-Disposition: attachment`, so a plain
// link downloads without leaving the page.
function DownloadPdfButton({
  resourceId,
  variant,
}: {
  resourceId: string;
  variant: 'secondary' | 'ghost';
}) {
  const t = useTranslations('Resources');
  return (
    <Button
      href={`/api/v1/resources/${resourceId}/pdf`}
      variant={variant}
      size="sm"
      icon={I.download}
      onClick={() => trackResourceFileDownloaded({ file_extension: 'pdf' })}
    >
      {t('detail.downloadPdf')}
    </Button>
  );
}

// Video lives in the structured `attachments` field — the markdown
// sanitizer strips raw `<iframe>` from the body, so this is the only path
// for video on a resource. Either a YouTube / Vimeo embed or a file we host.
function VideoEmbed({ resource }: { resource: ResourceDetailView }) {
  const video = resource.attachments.find((a) => a.kind === 'video');
  if (video === undefined) return null;
  if (video.provider === 'upload') {
    return (
      <HostedVideoPlayer
        url={video.url}
        mimeType={video.mimeType}
        title={resource.title}
      />
    );
  }
  return (
    <VideoEmbedFrame
      provider={video.provider}
      url={video.url}
      title={resource.title}
    />
  );
}

// Non-video attachments — files (PDFs, docs) + images. Renders below the
// body as a download list. Video attachments are handled by VideoEmbed.
function Attachments({ resource }: { resource: ResourceDetailView }) {
  const items = resource.attachments.filter(
    (a): a is Extract<ResourceAttachmentView, { kind: 'file' | 'image' }> =>
      a.kind === 'file' || a.kind === 'image',
  );
  if (items.length === 0) return null;
  return <AttachmentsList items={items} />;
}

function AttachmentsList({
  items,
}: {
  items: ReadonlyArray<Extract<ResourceAttachmentView, { kind: 'file' | 'image' }>>;
}) {
  const t = useTranslations('Resources');
  return (
    <Stack gap="md">
      <Eyebrow>{t('detail.attachments')}</Eyebrow>
      <Stack gap="sm">
        {items.map((att, idx) => {
          const displayName =
            att.name && att.name.trim().length > 0
              ? att.name
              : filenameFromUrl(att.url);
          return (
            <LinkUnstyled
              key={`${att.url}-${idx}`}
              href={att.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AttachmentCard
                title={displayName}
                kind={att.kind === 'image' ? t('attachments.image') : t('attachments.file')}
                {...(att.kind === 'image' ? { src: att.url } : {})}
              />
            </LinkUnstyled>
          );
        })}
      </Stack>
    </Stack>
  );
}

function filenameFromUrl(url: string): string {
  try {
    const decoded = decodeURIComponent(url);
    const segs = decoded.split('/');
    return segs[segs.length - 1] ?? url;
  } catch {
    return url;
  }
}

// ── Skeleton sub-components ───────────────────────────────────────────────
// Co-located with the loaded variants so the rhythms stay in sync — when
// the loaded body shifts (extra row, new section), its skeleton sibling is
// inches away.

function buildResourceHeaderSkeleton(): {
  readonly header: ReactNode;
  readonly cover: undefined;
} {
  const header = (
    <Stack gap="lg">
      <Skeleton width="60%" height={32} rounded="sm" />
      <Skeleton width="80%" height={20} rounded="sm" />
      <Row gap="md" align="center" wrap>
        <Row gap="xs" align="center">
          <Skeleton width={16} height={16} rounded="sm" />
          <Skeleton width={120} height={18} rounded="sm" />
        </Row>
        <Skeleton width={3} height={3} rounded="full" />
        <Row gap="xs" align="center">
          <Skeleton width={16} height={16} rounded="sm" />
          <Skeleton width={100} height={18} rounded="sm" />
        </Row>
      </Row>
    </Stack>
  );
  return { header, cover: undefined };
}

function ResourceArticleSkeleton() {
  return (
    <Card pad="flush" style={{ overflow: 'hidden' }}>
      <Stack gap="xl" p="2xl">
        <Row gap="md" align="center">
          <Skeleton
            width={44}
            height={44}
            rounded="full"
            style={{ flexShrink: 0 }}
          />
          <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
            <Skeleton width="35%" height={14} rounded="sm" />
            <Skeleton width="25%" height={12} rounded="sm" />
          </Stack>
        </Row>
        <Stack gap="lg">
          <SkeletonText lines={4} size="lg" />
          <SkeletonText lines={3} size="lg" />
          <SkeletonText lines={5} size="lg" />
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Aside ─────────────────────────────────────────────────────────────────

function buildAside(
  resource: ResourceDetailView | null,
  related: RelatedResources,
): ReactNode {
  if (resource === null) {
    return (
      <>
        <Card pad="compact">
          <Stack gap="md">
            <PanelHeaderSkeleton />
            <Stack gap="sm">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} width="75%" height={14} rounded="sm" />
              ))}
            </Stack>
          </Stack>
        </Card>
        <Card pad="compact">
          <Stack gap="md">
            <PanelHeaderSkeleton />
            <Row gap="xs" wrap>
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} width={72} height={26} rounded="full" />
              ))}
            </Row>
          </Stack>
        </Card>
        <RelatedSkeleton />
      </>
    );
  }

  const hasTopicsCard = resource.tags.length > 0 || resource.designedFor !== null;
  const relatedAside = buildRelatedAside(related);

  if (!hasTopicsCard && relatedAside === null) {
    return null;
  }

  return (
    <>
      {hasTopicsCard ? (
        <TopicsSection tags={resource.tags} designedFor={resource.designedFor} />
      ) : null}
      {relatedAside}
    </>
  );
}

function buildRelatedAside(related: RelatedResources): ReactNode {
  switch (related.status) {
    case 'pending':
      return <RelatedSkeleton />;
    case 'error':
      return <RelatedLoadError error={related.error} />;
    case 'success':
      return related.items.length > 0 ? <RelatedSection related={related.items} /> : null;
  }
}

function RelatedLoadError({ error }: { error: unknown }) {
  const t = useTranslations('Resources');
  return (
    <Card pad="compact">
      <LoadErrorState
        title={t('detail.relatedLoadError')}
        error={error}
        density="compact"
        bordered={false}
      />
    </Card>
  );
}

// Topics and the audience a resource was designed for share one card as two
// sections; each pill links to the list filtered to it.
function TopicsSection({
  tags,
  designedFor,
}: {
  tags: ReadonlyArray<{ slug: string; label: string }>;
  designedFor: ResourceDesignedFor | null;
}) {
  const t = useTranslations('Resources');
  return (
    <Card pad="compact">
      <Stack gap="xl">
        {tags.length > 0 ? (
          <Stack gap="md">
            <Eyebrow tone="accent">{t('detail.topics')}</Eyebrow>
            <Row gap="sm" rowGap="sm" wrap>
              {tags.map((tag) => (
                <Pill
                  key={tag.slug}
                  tone="burgundy"
                  size="sm"
                  href={`/resources?${new URLSearchParams({ tags: tag.slug }).toString()}`}
                  linkAs={RouterLinkAs}
                >
                  {tag.label}
                </Pill>
              ))}
            </Row>
          </Stack>
        ) : null}
        {designedFor !== null ? (
          <Stack gap="md">
            <Eyebrow tone="accent">{t('detail.audience')}</Eyebrow>
            <Row gap="sm" rowGap="sm" wrap>
              <Pill
                tone="burgundy"
                size="sm"
                href={`/resources?${new URLSearchParams({ designedFor }).toString()}`}
                linkAs={RouterLinkAs}
              >
                {t(`card.designedFor.${designedFor}`)}
              </Pill>
            </Row>
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}

function RelatedSkeleton() {
  return (
    <Card pad="compact">
      <Stack gap="md">
        <PanelHeaderSkeleton />
        <Stack gap="md">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} width="85%" height={14} rounded="sm" />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function RelatedSection({ related }: { related: ReadonlyArray<RelatedResource> }) {
  const t = useTranslations('Resources');
  return (
    <Card pad="compact">
      <Stack gap="md">
        <Eyebrow tone="accent">{t('detail.related')}</Eyebrow>
        <Stack gap="md">
          {related.map((r) => (
            <RouterLinkAs
              key={r.id}
              href={`/resources/${r.id}`}
              aria-label={t('list.card.openResource', { title: r.title })}
            >
              <Row gap="md" align="center" justify="space-between">
                {/* eslint-disable-next-line @leanin/ui/typography-component-required -- same semibold title exception as the preview cards */}
                <h3 className={styles.relatedTitle}>{r.title}</h3>
                <Icon size={14} color="var(--muted-foreground)">{I.arrow}</Icon>
              </Row>
            </RouterLinkAs>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

